import { format, formatDistanceToNow } from "date-fns";
import { ArrowRight, CircleDot } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useEventStatusHistory } from "@/hooks/useEventStatusHistory";
import { EVENT_STATUS_LABELS, EVENT_STATUS_BADGE } from "@/hooks/useEvents";

interface EventStatusHistoryTimelineProps {
  eventId: string;
}

export function EventStatusHistoryTimeline({ eventId }: EventStatusHistoryTimelineProps) {
  const { data: history, isLoading } = useEventStatusHistory(eventId);

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  if (!history || history.length === 0) {
    return <p className="text-sm text-muted-foreground">No status history yet.</p>;
  }

  return (
    <ol className="space-y-4 relative border-l border-border pl-6 ml-2">
      {history.map((entry) => (
        <li key={entry.id} className="relative">
          <CircleDot className="absolute -left-[31px] top-0.5 h-4 w-4 text-muted-foreground bg-background" />
          <div className="flex items-center gap-2 flex-wrap">
            {entry.from_status && (
              <>
                <Badge
                  variant="outline"
                  className={`${EVENT_STATUS_BADGE[entry.from_status]} capitalize text-xs`}
                >
                  {EVENT_STATUS_LABELS[entry.from_status]}
                </Badge>
                <ArrowRight className="h-3 w-3 text-muted-foreground" />
              </>
            )}
            <Badge
              variant="outline"
              className={`${EVENT_STATUS_BADGE[entry.to_status]} capitalize text-xs`}
            >
              {EVENT_STATUS_LABELS[entry.to_status]}
            </Badge>
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            {format(new Date(entry.changed_at), "MMM d, yyyy 'at' h:mm a")}
            {" — "}
            <span>{formatDistanceToNow(new Date(entry.changed_at), { addSuffix: true })}</span>
            {entry.changed_by_name && (
              <>
                {" by "}
                <span className="font-medium text-foreground">{entry.changed_by_name}</span>
              </>
            )}
          </div>
          {entry.note && (
            <p className="mt-1 text-sm text-muted-foreground italic">{entry.note}</p>
          )}
        </li>
      ))}
    </ol>
  );
}
